import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { PkgState, fmtEur, fmtHours } from "@/lib/reports";
import { HoursInput } from "@/components/common/HoursInput";
import { Panel, SectionLabel } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const today = () => new Date().toISOString().slice(0, 10);
const F = ({ l, children }) => <div className="space-y-1.5"><Label className="text-xs">{l}</Label>{children}</div>;

const NewPackage = ({ open, onOpenChange, companyId, onDone }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ name: "", hours: null, value: "", purchase_date: today(), expires_at: "", notes: "" }); }, [open]);
  const s = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try { await api.post("/hour-packages", { company_id: companyId, name: f.name, hours: (f.hours || 0) / 60, value: Number(f.value), purchase_date: f.purchase_date, expires_at: f.expires_at || null, notes: f.notes || null });
      toast.success("Pacchetto ore creato"); onOpenChange(false); onDone(); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="sm:max-w-md" data-testid="package-dialog">
      <DialogHeader><DialogTitle>Nuovo pacchetto ore</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <F l="Nome"><Input value={f.name || ""} onChange={s("name")} maxLength={200} placeholder="Es. Pacchetto assistenza 20h" data-testid="package-name" /></F>
        <div className="grid grid-cols-2 gap-3"><F l="Ore acquistate"><HoursInput value={f.hours ?? null} onChange={(v) => setF((x) => ({ ...x, hours: v }))} className="w-24" testId="package-hours" /></F>
          <F l="Valore (imponibile, €)"><Input type="number" min={0} step="0.01" value={f.value || ""} onChange={s("value")} data-testid="package-value" /></F></div>
        <div className="grid grid-cols-2 gap-3"><F l="Data acquisto"><Input type="date" value={f.purchase_date || ""} onChange={s("purchase_date")} data-testid="package-purchase" /></F>
          <F l="Scadenza (facoltativa)"><Input type="date" value={f.expires_at || ""} onChange={s("expires_at")} data-testid="package-expires" /></F></div>
        <F l="Note"><Input value={f.notes || ""} onChange={s("notes")} maxLength={2000} data-testid="package-notes" /></F>
      </div>
      <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
        <Button onClick={save} disabled={busy || !f.name?.trim() || !(Number(f.hours) > 0) || !(Number(f.value) > 0) || !f.purchase_date} data-testid="package-dialog-save">Crea pacchetto</Button></DialogFooter>
    </DialogContent></Dialog>
  );
};

const Movements = ({ pkg }) => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(`/hour-packages/${pkg.id}/movements`).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [pkg.id]);
  return (
    <ul className="mt-3 divide-y divide-line/60 rounded-control bg-slate-50 px-3 text-xs" data-testid={`package-movements-${pkg.id}`}>
      {(items || []).map((m) => <li key={m.id} className="flex flex-wrap justify-between gap-2 py-2">
        <span><b className="uppercase">{m.kind}</b> · {fmtDateTime(m.at)}{m.report_number ? ` · ${m.report_number}` : ""} · {m.user_name || "—"}{m.reason ? ` · ${m.reason}` : ""}</span>
        <span className="font-mono">{m.hours > 0 ? "+" : ""}{fmtHours(m.hours)} · {m.value > 0 ? "+" : ""}{fmtEur(m.value)} → {fmtHours(m.hours_after)} / {fmtEur(m.value_after)}</span></li>)}
      {items && !items.length && <li className="py-2 text-slate-500">Nessun movimento.</li>}
    </ul>
  );
};

export const CompanyPackagesTab = ({ company }) => {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false), [exp, setExp] = useState(null);
  const load = useCallback(() => api.get("/hour-packages", { params: { company_id: company.id } }).then((r) => setItems(r.data.items)).catch(() => setItems([])), [company.id]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (p) => { try { await api.patch(`/hour-packages/${p.id}`, { status: p.status === "chiuso" ? "attivo" : "chiuso" }); load(); } catch (e) { toast.error(errorMessage(e)); } };
  return (
    <Panel className="p-5" data-testid="company-packages-tab">
      <div className="mb-3 flex items-center justify-between"><SectionLabel>Pacchetti ore</SectionLabel>
        {can("packages:manage") && <Button size="sm" className="gap-1" onClick={() => setOpen(true)} data-testid="package-new"><Plus className="h-4 w-4" />Nuovo pacchetto</Button>}</div>
      {items && !items.length && <p className="text-sm text-slate-500">Nessun pacchetto ore per questo cliente.</p>}
      <ul className="divide-y divide-line">{(items || []).map((p) => (
        <li key={p.id} className="py-3" data-testid={`package-row-${p.id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <button className="min-w-0 flex-1 text-left" onClick={() => setExp(exp === p.id ? null : p.id)} data-testid={`package-toggle-${p.id}`}>
              <p className="font-mono text-xs font-semibold text-slate-400">{p.number} · acquisto {p.purchase_date}{p.expires_at ? ` · scade ${p.expires_at}` : ""}</p>
              <p className="text-sm font-semibold text-ink">{p.name}</p></button>
            <span className="text-xs text-slate-600" data-testid={`package-hours-${p.id}`}>Ore <b className="text-ink">{fmtHours(p.hours_remaining)}</b> / {fmtHours(p.hours_purchased)}</span>
            <span className="text-xs text-slate-600" data-testid={`package-value-${p.id}`}>Valore <b className="text-ink">{fmtEur(p.value_remaining)}</b> / {fmtEur(p.value_purchased)}</span>
            <PkgState state={p.state} testId={`package-state-${p.id}`} />
            {can("packages:manage") && p.status !== "esaurito" && <Button size="sm" variant="ghost" onClick={() => toggle(p)} data-testid={`package-close-${p.id}`}>{p.status === "chiuso" ? "Riattiva" : "Chiudi"}</Button>}
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, (p.value_remaining / p.value_purchased) * 100)}%` }} /></div>
          {exp === p.id && <Movements pkg={p} />}
        </li>))}</ul>
      <NewPackage open={open} onOpenChange={setOpen} companyId={company.id} onDone={load} />
    </Panel>
  );
};
